import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NudosFileInput, NudosLink, NudosTextInput } from '../../../../../../../components/NudosComponents';
import NudosCountrySelect from '../../../../../../../components/NudosComponents/NudosCountrySelect/NudosCountrySelect';
import { ICountryDetail } from '../../../../../../../types/countries';
import {
  IBillingData,
  IBillingForm,
  ISpecialBusinessNameTypes,
  ISpecialBusinessNameTypesByCountry
} from '../../../../../../../types/bussines';
import errorMessage from '../../../../../../../utils/errorMessage';
import alertMessage from '../../../../../../../utils/alertMessage';
import { updateBillingDataByCountry, updateSpecialBusinessNameType } from '../../../../../../../services/bussines';
import { getTaxDocumentText } from '../../../../../../../utils/formValidations';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import { BlackCheck } from '../../../../../../countriesList/components';
import { capitalize } from '../../../../../../../utils/truncateText';
import NudosConfirmActionModal from '../../../../../../../components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { specialBusiness } from '../../../../../../../utils/productDefinitions';
import { getSpecialBusinessNameText } from '../../../../../../../utils/billingData';
import './BillingCountry.scss';

const BillingCountry = ({
  data,
  countriesList,
  organizationName,
  updateWithoutLoaders,
  specialBusinessTypes
}: {
  data: IBillingData;
  countriesList: ICountryDetail[];
  organizationName?: string;
  specialBusinessTypes?: ISpecialBusinessNameTypes;
  updateWithoutLoaders: (disabledLoader?: boolean) => void;
}) => {
  const { organizationId } = useParams<{ organizationId: string }>();

  const [formInfo, setFormInfo] = useState<IBillingForm>();
  const [defaultCountryDocument, setDefaultCountryDocument] = useState<ICountryDetail>();
  const [taxSituationDocument, setTaxSituationDocument] = useState<File>();
  const [loading, setLoading] = useState(false);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [specialBusinessNameToEditIsChecked, setSpecialBusinessNameToEditIsChecked] = useState(false);
  const [specialBusinessTypeData, setSpecialBusinessTypeData] = useState<ISpecialBusinessNameTypesByCountry>();

  const { countryCode, specialBusinessNameTypeId } = data;
  const isSpecialBusiness = specialBusiness?.includes(countryCode || '');
  const markActionText = specialBusinessNameToEditIsChecked ? 'desmarcar' : 'marcar';

  const specialBusinessNameText = getSpecialBusinessNameText(specialBusinessTypeData);

  const confirmActionModalText = `Estás a punto de ${markActionText} la Razón Social de la empresa ${
    data?.bussinesName
  } como ${capitalize(specialBusinessNameText)} ¿seguro que deseas continuar?`;

  const defaultData = () => {
    if (data) {
      const initialData = {
        countryId: countriesList?.find(country => country?.name === data?.country)?.id || 0,
        documentCountryId: countriesList?.find(country => country?.name === data?.country)?.id || 0,
        name: organizationName || '',
        businessName: data?.bussinesName || '',
        document: data?.document || '',
        city: data?.city || '',
        zipCode: data?.zipCode || '',
        state: data?.state || '',
        additionalReferences: data?.additionalReferences || '',
        email: data?.email || '',
        address: data?.address || '',
        taxSituationDocUrl: data?.taxSituationDocUrl || '',
        taxSituationId: data?.taxSituationId,
        taxUseId: data?.taxUseId,
        countryCode: data?.countryCode,
        specialBusinessNameTypeId: data?.specialBusinessNameTypeId
      };
      setFormInfo({ ...initialData });
      if (data?.countryDocument && data?.countryFlagDocument) {
        setDefaultCountryDocument({ flag: data?.countryFlagDocument, name: data?.countryDocument });
      }
    }
  };

  const isDocumentUploadDisabled = () => {
    if (!organizationId || loading || !formInfo?.name) return true;
    if (!formInfo?.countryId || !formInfo?.documentCountryId) return true;
    if (data.countryDocument === 'México' && (!formInfo?.taxSituationId || !formInfo?.taxUseId)) return true;
  };

  const handleSaveBillingData = async (newTaxSituationDocument?: File) => {
    if (!newTaxSituationDocument) return;
    if (isDocumentUploadDisabled()) return;
    setTaxSituationDocument(newTaxSituationDocument);
    const body = {
      countryId: formInfo?.countryId || 0,
      name: formInfo?.name,
      businessName: formInfo?.businessName?.trim() || '',
      document: formInfo?.document?.trim() || '',
      documentCountryId: formInfo?.documentCountryId,
      address: formInfo?.address?.trim() || '',
      email: formInfo?.email?.trim() || '',
      additionalReferences: formInfo?.additionalReferences?.trim() || '',
      zipCode: formInfo?.zipCode?.trim() || '',
      city: formInfo?.city?.trim() || '',
      state: formInfo?.state?.trim() || '',
      taxSituationDocUrl: formInfo?.taxSituationDocUrl,
      taxSituationId: formInfo?.taxSituationId || undefined,
      taxUseId: formInfo?.taxUseId || undefined,
      countryCode: data?.countryCode,
      specialBusinessNameTypeId: data?.specialBusinessNameTypeId
    };
    setLoading(true);
    const formData = new FormData();
    const bodyJSON = JSON.stringify(body);
    formData.append('body', bodyJSON);
    formData.append('file', newTaxSituationDocument);
    try {
      await updateBillingDataByCountry(organizationId, formData);
      updateWithoutLoaders && updateWithoutLoaders(true);
      alertMessage('Se cargó el documento exitosamente');
      setLoading(false);
    } catch (error) {
      errorMessage();
      setLoading(false);
    }
  };

  const handleUpdateSpecialBussiness = async (
    specialBusinessNameToEditIsChecked: boolean,
    specialBusinessType?: ISpecialBusinessNameTypesByCountry
  ) => {
    try {
      const body = {
        billingDataId: data?.id,
        specialBusinessNameTypeId: specialBusinessNameToEditIsChecked ? undefined : specialBusinessType?.id
      };
      await updateSpecialBusinessNameType(body);
      updateWithoutLoaders(false);
      displaySuccessNotification({
        customJSXMessage: (
          <>
            Empresa {specialBusinessNameToEditIsChecked ? 'desmarcada' : 'marcada'} como{' '}
            {capitalize(specialBusinessNameText)} con éxito
          </>
        )
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setOpenActionModal(false);
      setSpecialBusinessNameToEditIsChecked(false);
    }
  };

  useEffect(() => {
    defaultData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div id="billingCountry">
      <div className="headerCountry">
        <div className="headerCountry">
          <div className="containerCountry">
            <div className="billingFlagBox">
              <div className="myFlag" style={{ backgroundImage: `url(${data?.flag})` }} />
            </div>
            <div className="nameCountry">{data?.country}</div>
          </div>
        </div>
      </div>

      <div className="fieldsLine firstLine">
        <NudosCountrySelect
          componentSize="extraSmall"
          isDisabled
          label="País del Doc."
          defaultCountry={defaultCountryDocument || undefined}
          listOfCountries={countriesList}
        />
        <NudosTextInput
          componentSize="small"
          isDeactivated
          label="Documento de identificación fiscal*"
          placeholder="RUT, CUIT, NIT "
          defaultValue={formInfo?.document}
          handleChange={e => setFormInfo({ ...formInfo, document: e })}
          isFilled={!!formInfo?.document}
        />
        <NudosTextInput
          componentSize="small"
          isDeactivated
          label="Razón social o denominación*"
          isFilled={!!formInfo?.businessName}
          placeholder="Razón social"
          defaultValue={formInfo?.businessName}
          handleChange={e => setFormInfo({ ...formInfo, businessName: e })}
        />
        <NudosTextInput
          componentSize="small"
          isDeactivated
          label="Ciudad*"
          placeholder="Ciudad"
          defaultValue={formInfo?.city}
          handleChange={e => setFormInfo({ ...formInfo, city: e })}
          isFilled={!!formInfo?.city}
        />
        <NudosTextInput
          componentSize="extraSmall"
          isDeactivated
          label="CP*"
          placeholder="Zip/ CP"
          defaultValue={formInfo?.zipCode}
          handleChange={e => setFormInfo({ ...formInfo, zipCode: e })}
          isFilled={!!formInfo?.zipCode}
        />
        <NudosTextInput
          componentSize="small"
          isDeactivated
          label="Estado/Provincia / Departament"
          placeholder="Estado/ Provincia/ Dpto"
          defaultValue={formInfo?.state}
          handleChange={e => setFormInfo({ ...formInfo, state: e })}
          isFilled={!!formInfo?.state}
        />
      </div>
      <div className="fieldsLine secondLine">
        <NudosTextInput
          componentSize="large"
          isDeactivated
          label="Dirección de facturación*"
          placeholder="Escribe la dirección de facturación"
          defaultValue={formInfo?.address}
          handleChange={e => setFormInfo({ ...formInfo, address: e })}
          isFilled={!!formInfo?.address}
        />
        <NudosTextInput
          componentSize="medium"
          isDeactivated
          label="Indicaciones"
          placeholder="Apartamento, Piso, Bloque."
          defaultValue={formInfo?.additionalReferences}
          handleChange={e => setFormInfo({ ...formInfo, additionalReferences: e })}
          isFilled={!!formInfo?.additionalReferences}
        />
        <NudosTextInput
          componentSize="small"
          isDeactivated
          label="Correo de facturación*"
          placeholder="Correo de facturación"
          defaultValue={formInfo?.email}
          handleChange={e => setFormInfo({ ...formInfo, email: e })}
          isFilled={!!formInfo?.email}
        />
      </div>
      <div className="fieldsLine thirdLine">
        <NudosFileInput
          uploadedFileHandler={handleSaveBillingData}
          buttonText={getTaxDocumentText(data.countryDocument)}
          customWidth="280px"
          isDisabled={isDocumentUploadDisabled()}
          isLoading={loading}
          isFilled={!!data?.taxSituationDocUrl || !!taxSituationDocument}
          color="orange"
        />
        <NudosLink inactiveLinkText="Ver documento" activeLinkText="Ver documento" linkUrl={data?.taxSituationDocUrl} />
      </div>
      {isSpecialBusiness && (
        <div className="specialBusinessContainer">
          Casos especiales
          {specialBusinessTypes?.[data?.countryCode || '']?.map(specialBusinessType => {
            const isChecked = specialBusinessNameTypeId === specialBusinessType?.id;
            const disabledOption =
              specialBusinessNameTypeId && specialBusinessNameTypeId !== specialBusinessType?.id ? 'disabled' : '';
            const specialStyles = isChecked ? 'checked' : disabledOption;
            const classnames = `specialBusinessOption ${specialStyles}`;
            const specialBusinessNameText = getSpecialBusinessNameText(specialBusinessType);
            const handleChangeCheck = () => {
              setSpecialBusinessNameToEditIsChecked(isChecked);
              setOpenActionModal(true);
              setSpecialBusinessTypeData(specialBusinessType);
            };
            return (
              <div className={classnames} key={`special-option-${specialBusinessType?.id}`}>
                <BlackCheck handleChangeCheck={handleChangeCheck} checked={isChecked} />
                {capitalize(specialBusinessNameText)} - {specialBusinessType?.description} ⚠️
              </div>
            );
          })}
        </div>
      )}
      {openActionModal && (
        <NudosConfirmActionModal
          modalText={confirmActionModalText}
          customHeight="auto"
          firstButtonText="No, cancelar"
          secondButtonText={`Sí, ${markActionText} como ${capitalize(specialBusinessNameText)}`}
          firstButtonAction={() => setOpenActionModal(false)}
          secondButtonAction={() =>
            handleUpdateSpecialBussiness(specialBusinessNameToEditIsChecked, specialBusinessTypeData)
          }
        />
      )}
    </div>
  );
};
export default BillingCountry;
